import React, { useState } from "react";
import gql from "graphql-tag";
import { Box, Text, Flex, ChevronRight, TouchableOpacity } from "@atoms";
import { ImageCircle, NoAvatar } from "@molecules";
import { constants } from "@services";
import { useStoreAppValue } from "@hooks";
import { FundsPageFundraiserQuery } from "../FundsPageFundraiser";
import { FundsPageEarningsFundraisersListItemFundraiserFragment } from "./__generated__/FundsPageEarningsFundraisersListItemFundraiserFragment";

// *** commented out and make type STRING because the logic below threw an error when using the codegen type
// import { FundsPageEarningsFundraisersListItemPrivacyLevel } from "./__generated__/FundsPageEarningsFundraisersListItemPrivacyLevel";

// == Types ================================================================

interface IProps {
  fundraiser: FundsPageEarningsFundraisersListItemFundraiserFragment;
  isViewerFundraiser: boolean;
  privacyLevel: string;
}

// == Constants ============================================================

FundsPageEarningsFundraisersListItem.fragments = {
  fundraiser: gql`
    fragment FundsPageEarningsFundraisersListItemFundraiserFragment on Fundraiser {
      id
      userId
      name
      firstName
      lastName
      avatarImage
      isOwner
      joinedAt
      deactivatedAt
      amountRaised {
        dollarsAmount
        formatted
      }
    }
  `,
  privacyLevel: gql`
    fragment FundsPageEarningsFundraisersListItemPrivacyLevel on Campaign {
      id
      privacyLevel
    }
  `,
};

// == Component ============================================================

export default function FundsPageEarningsFundraisersListItem({
  fundraiser,
  isViewerFundraiser,
  privacyLevel,
}: IProps) {
  const [isOpen, setOpen] = useState(false);
  const { currentUser, isMember } = useStoreAppValue();
  const {
    DEFAULT_USER_AVATAR_STRING,
    PRIVACY_LEVEL_TYPES: { ANONYMOUS },
  } = constants;
  const {
    id,
    userId,
    name,
    firstName,
    lastName,
    avatarImage,
    isOwner,
    joinedAt,
    deactivatedAt,
    amountRaised,
  } = fundraiser;

  const isAvatarImage = !avatarImage?.includes(DEFAULT_USER_AVATAR_STRING);
  const isDisabled = !!joinedAt && !!deactivatedAt;
  const isPending = !joinedAt && name !== id;
  const isDisabledAndNoMoneyRaised = isDisabled && amountRaised.dollarsAmount === 0;
  const isCampaignAnonymous = privacyLevel === ANONYMOUS;
  const circleDiameter = 50;

  const renderName = () => {
    if (name === id) return `${name} (Team)`;
    if (isViewerFundraiser && isCampaignAnonymous) return `${name} (You)`;
    return name;
  };

  if (isPending || isDisabledAndNoMoneyRaised) return null;

  return (
    <>
      <TouchableOpacity
        containerProps={{ style: { cursor: currentUser && isMember ? `pointer` : `default` } }}
        onTouch={currentUser && isMember ? () => setOpen(true) : null}
      >
        <Flex
          borderRadius={isViewerFundraiser ? "5px" : ""}
          justifyContent="space-between"
          opacity={isDisabled ? 0.5 : 1}
          padding={3}
          width={1}
        >
          <Flex alignItems="center">
            <Box>
              {!!avatarImage && !!isAvatarImage ? (
                <ImageCircle
                  alt={name}
                  border={isViewerFundraiser ? "3px solid #48B1BF" : ""}
                  faded={!!deactivatedAt}
                  height={circleDiameter}
                  src={avatarImage}
                  width={circleDiameter}
                />
              ) : (
                <NoAvatar
                  circleDiameter={circleDiameter}
                  firstName={firstName}
                  isHighlighted={isViewerFundraiser}
                  lastName={lastName}
                />
              )}
            </Box>
            <Flex flexDirection="column" paddingLeft={3}>
              <Text display="block" fontSize={[1, 2]}>
                {renderName()}
              </Text>
              {!!isOwner && (
                <Text color="primary" display="block" fontSize={[0, 1]}>
                  Captain
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex alignItems="center" marginLeft={2}>
            <Text fontSize={[1, 2]} paddingRight={2}>
              {amountRaised.formatted}
            </Text>
            {!!currentUser && isMember && <ChevronRight color="#666" size="xs" />}
          </Flex>
        </Flex>
      </TouchableOpacity>
      {isOpen && (
        <FundsPageFundraiserQuery
          id={id}
          isOpen={isOpen}
          name={name}
          setOpen={setOpen}
          userId={userId}
        />
      )}
    </>
  );
}

// == Styles ===============================================================
